import FilterV3 from '@components/FilterV3/FilterV3';
import AddFiltersButton from '@components/FilterV3/components/AddFiltersButton/AddFiltersButton';
import { FilterV3ContextProvider } from '@components/FilterV3/contexts/FilterV3.context';
import useWidth from '@components/Hooks/useWidth';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import Breadcrumb from '@components/v2/ui/Breadcrumb/Breadcrumb';
import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Loading from '@components/v2/ui/Loading/Loading';
import { Box, Stack } from '@mui/material';
import { Download, Share2 } from 'lucide-react';
import { Redirect, useParams } from 'react-router-dom';
import { useUserContextPreferences } from '../../../../hooks/useUserContextPreferences';
import LangEnum from '../../../../lang/LangEnum';
import PATHS from '../../../../routes/routes.const';
import { RoutePageProps } from '../../../../routes/routes.types';
import { WithId } from '../../../../types/Base.types';
import { CatalogProductsTestIds } from '../../../../utils/testIds/CatalogProducts.testIds';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import { ProductLockProvider } from '../../../products/products-details-v2/hooks/useProductLock';
import ShareCatalogModal from '../../../shared-entities/ShareCatalogModal';
import { useTranslation } from '../../../translation/TranslationContext';
import CatalogExportModal from '../../components/CatalogExportModal/CatalogExportModal';
import { CatalogSectionTabsSlot } from '../v2/components/CatalogSectionTabsSlot/CatalogSectionTabsSlot';
import CatalogViewSettings from '../v2/components/CatalogViewSettings/CatalogViewSettings';
import CatalogMassActionsProvider from '../v2/contexts/CatalogMassActions.context';
import { CatalogSectionsProvider } from '../v2/contexts/CatalogSections.context';
import { CatalogViewProvider } from '../v2/contexts/CatalogView.context';
import { CatalogViewGroupBy } from '../v2/view/catalogView.types';
import { CatalogHeaderAction } from './catalogHeaderActions.types';
import CatalogBanner from './components/CatalogBanner/CatalogBanner';
import CatalogDetailsSlotContent from './components/CatalogDetailsSlotContent';
import CatalogListLockButton from './components/CatalogListLockButton/CatalogListLockButton';
import CatalogListPlaceholder from './components/CatalogListPlaceholder';
import CatalogMoreActionsButton from './components/CatalogMoreActionsButton/CatalogMoreActionsButton';
import LiveShopButton from './components/LiveShopButton/LiveShopButton';
import useCatalogLiveShop from './hooks/useCatalogLiveShop';
import useCatalogProductsScreen from './hooks/useCatalogProductsScreen';
import useCatalogSidePanel from './hooks/useCatalogSidePanel';

interface CatalogProductsScreenProps extends RoutePageProps, UsedInOfflineMode {
  canPersist?: boolean;
}

interface BuildCatalogHeaderActionsParams {
  canExport?: boolean;
  showShare?: boolean;
  onExport: () => void;
  onShare: () => void;
}

const buildCatalogHeaderActions = ({
  canExport,
  showShare,
  onExport,
  onShare,
}: BuildCatalogHeaderActionsParams): CatalogHeaderAction[] => {
  const actions: CatalogHeaderAction[] = [];

  if (canExport) {
    actions.push({ label: LangEnum.GLOBAL_EXPORT, icon: Download, onClick: onExport });
  }

  if (showShare) {
    actions.push({ label: LangEnum.GLOBAL_SHARE, icon: Share2, onClick: onShare });
  }

  return actions;
};

const CatalogProductsScreen = (props: CatalogProductsScreenProps) => {
  const { id } = useParams<WithId>();
  const { isOfflineMode = false, canPersist = false } = props;
  const { translate } = useTranslation();
  const {
    catalog,
    catalogConfig,
    isCatalogSettled,
    sections,
    canExport,
    canEditCatalogs,
    readMode,
    totalItems,
    isDynamic,
    showShare,
    showDetailsToggle,
    breadcrumbItems,
    filters,
    handleExport,
    handleShare,
    isShareOpen,
    closeShare,
  } = useCatalogProductsScreen({ catalogId: id, isOfflineMode, canPersist });
  const liveShop = useCatalogLiveShop({
    liveLink: catalog?.liveLink,
    isOfflineMode,
  });
  const sidePanel = useCatalogSidePanel({ isDetailsAvailable: showDetailsToggle });
  const { mobileWidth: isPhone } = useWidth();
  const { getDefaultCatalogView, hasSettledUserPreferences } = useUserContextPreferences();

  const headerActions = buildCatalogHeaderActions({
    canExport,
    showShare,
    onExport: handleExport,
    onShare: handleShare,
  });

  const userPreference = getDefaultCatalogView();

  const isPreferencesSettled = !canPersist || hasSettledUserPreferences;

  if (!isCatalogSettled && !catalog) {
    return (
      <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Loading testId={CatalogProductsTestIds.SCREEN_LOADING} />
      </Stack>
    );
  }

  if (catalog?.isDefault) {
    return <Redirect to={PATHS.main.products.path} />;
  }

  const screen = (
    <FilterV3ContextProvider options={filters.options}>
      <CatalogViewProvider
        key={id}
        userPreference={{ data: userPreference, isSettled: isPreferencesSettled }}
        catalogConfig={{ data: catalogConfig, isSettled: isCatalogSettled }}
        isOfflineMode={isOfflineMode}
      >
        <ObjectScreen
          objectId={id}
          objectType="catalog"
          hasDetailsToggle={false}
          hasStickyFilterBar
          isDetailsOpen={sidePanel.isOpen}
          isDetailsPanelInlineOnTablet
          onDetailsOpenChange={sidePanel.handleOpenChange}
        >
          <ObjectScreen.Header
            breadcrumb={<Breadcrumb items={breadcrumbItems} />}
            actions={
              <>
                {!isPhone &&
                  headerActions.map((action) => (
                    <Button
                      key={action.label}
                      variant="text"
                      color="secondary"
                      size="medium"
                      startIcon={<Icon icon={action.icon} size="xs" />}
                      onClick={action.onClick}
                    >
                      {translate(action.label)}
                    </Button>
                  ))}
                <CatalogMoreActionsButton
                  catalogId={id}
                  isCatalogActive={catalog?.active}
                  isReadOnly={readMode}
                  isCompact={isPhone}
                  groupedActions={isPhone ? headerActions : undefined}
                />
              </>
            }
          />

          <ObjectScreen.Tabs>
            <CatalogSectionTabsSlot
              catalogId={id}
              sections={sections}
              isCatalogSettled={isCatalogSettled}
              isPreferencesSettled={isPreferencesSettled}
              canPersist={canPersist}
              readMode={readMode}
            />
          </ObjectScreen.Tabs>

          <ObjectScreen.Slot name="afterFilterBar">
            <CatalogBanner catalogId={id} isReadOnly={readMode} isOfflineMode={isOfflineMode} />
          </ObjectScreen.Slot>

          <ObjectScreen.Content>
            {() => (
              <CatalogMassActionsProvider
                readMode={readMode}
                totalItems={totalItems}
                isDynamic={isDynamic}
                catalogId={id}
                canPersist={canPersist}
              >
                <ProductLockProvider>
                  <ObjectScreen.Controls>
                    {liveShop.isAvailable && (
                      <LiveShopButton
                        isActive={sidePanel.activeContent === 'liveShop'}
                        onClick={() => sidePanel.toggle('liveShop')}
                      />
                    )}
                    <AddFiltersButton label={translate(LangEnum.GLOBAL_FILTER)} />
                    <CatalogViewSettings
                      canEditCatalogConfig={canEditCatalogs && canPersist}
                      canPersist={canPersist}
                      catalogId={id}
                      catalogConfig={catalogConfig}
                      isManualGroupingCriterion={catalog?.viewGroupBy === CatalogViewGroupBy.MANUAL}
                      hasSavedUserPreference={userPreference !== null}
                      isCatalogSettled={isCatalogSettled}
                      isPreferencesSettled={isPreferencesSettled}
                      isOfflineMode={isOfflineMode}
                    />
                    <CatalogListLockButton />
                    {showDetailsToggle && (
                      <ObjectScreen.DetailsToggle
                        isActive={sidePanel.activeContent === 'details'}
                        disabled={!catalog}
                        testId={CatalogProductsTestIds.DETAILS_BUTTON}
                        onClick={() => sidePanel.toggle('details')}
                      />
                    )}
                  </ObjectScreen.Controls>

                  <ObjectScreen.FilterBar>
                    <FilterV3
                      unifiedBar
                      list={filters.preferenceList}
                      options={filters.options}
                      searchField={filters.searchField}
                      isSearchActive={filters.isSearchActive}
                      onClearSearch={filters.onClearSearch}
                    />
                  </ObjectScreen.FilterBar>

                  <CatalogListPlaceholder
                    catalogId={id}
                    isCatalogSettled={isCatalogSettled}
                    isPreferencesSettled={isPreferencesSettled}
                    readMode={readMode}
                    isOfflineMode={isOfflineMode}
                    canPersist={canPersist}
                  />
                </ProductLockProvider>
              </CatalogMassActionsProvider>
            )}
          </ObjectScreen.Content>

          <ObjectScreen.DetailsPanel>
            <CatalogDetailsSlotContent
              activeContent={sidePanel.activeContent}
              videoId={liveShop.videoId}
              embedDomain={liveShop.embedDomain}
              catalog={catalog}
              isReadOnly={readMode}
            />
          </ObjectScreen.DetailsPanel>
        </ObjectScreen>
      </CatalogViewProvider>

      <CatalogExportModal />

      {catalog?.id && (
        <ShareCatalogModal
          isOpen={isShareOpen}
          catalogId={catalog.id}
          catalogName={catalog.name}
          enablePublicSharing={!!catalog.enablePublicSharing && !catalog.isDefault}
          onClose={closeShare}
        />
      )}
    </FilterV3ContextProvider>
  );

  const content = <CatalogSectionsProvider>{screen}</CatalogSectionsProvider>;

  if (isOfflineMode) {
    return (
      <Box className={props.rootStyles?.classes.root} sx={{ boxSizing: 'border-box' }}>
        {content}
      </Box>
    );
  }

  return content;
};

export default CatalogProductsScreen;
